import { useContext, useEffect, useState } from "react";
import "./ProfileDetails.scss";
import EditIcon from "@mui/icons-material/Edit";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import DescriptionIcon from "@mui/icons-material/Description";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import PersonIcon from "@mui/icons-material/Person";
import { useNavigate } from "react-router-dom";
import Buttons from "../../../components/Buttons/Buttons";
import UserAvatar from "../../../components/UserAvatar/UserAvatar";
import { UserContext } from "../../../store/UserContext";

const ProfileDetails = () => {
  const { loginUserDetails, getLoginUserDetails } = useContext(UserContext);
  const [patchData, setPatchData] = useState();
  const [userId, setUserId] = useState(null);
  const [superAdminData, setSuperAdminData] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState("");

  const navigate = useNavigate();
  const isSuperAdmin = userId === 1;

  const handleEditForm = () => {
    if (isSuperAdmin) {
      navigate("/superadmin-edit");
    } else {
      navigate("/user-form");
    }
  };

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedEmail(text);
    setTimeout(() => setCopiedEmail(""), 2000);
  };

  useEffect(() => {
    getLoginUserDetails();
    const storedUserId = localStorage.getItem("userId");
    setUserId(storedUserId ? parseInt(storedUserId) : null);
  }, [getLoginUserDetails]);

  useEffect(() => {
    if (isSuperAdmin && loginUserDetails?.currentSuperAdminModel) {
      setSuperAdminData(loginUserDetails?.currentSuperAdminModel);
    } else if (loginUserDetails?.currentHealthProfessionalModel != null) {
      setPatchData(loginUserDetails?.currentHealthProfessionalModel);
    } else if (loginUserDetails?.currentPatientModel != null) {
      setPatchData(loginUserDetails?.currentPatientModel);
    }
  }, [loginUserDetails, isSuperAdmin]);

  const profileName = isSuperAdmin
    ? superAdminData?.name || "Administrator"
    : patchData?.name || "";

  const profileRole = isSuperAdmin
    ? "Super Administrator"
    : patchData?.position || "Healthcare Professional";

  const primaryEmail = isSuperAdmin
    ? superAdminData?.email
    : patchData?.email;

  const username = isSuperAdmin ? superAdminData?.userName : null;

  const phone = isSuperAdmin ? null : patchData?.mobileNumber;

  const secondaryEmails = isSuperAdmin
    ? superAdminData?.secondaryEmails || []
    : patchData?.emails || [];

  const workplace = isSuperAdmin ? "Global Operations" : patchData?.workPlace || "-";
  const prefix = isSuperAdmin ? "ADMIN" : patchData?.prefix || "-";
  const userCount = isSuperAdmin ? "All" : patchData?.numberOfUsers ?? "-";
  const rawDescription = isSuperAdmin
    ? "Full administrative system access and platform management permissions."
    : patchData?.description;

  const cleanDescription =
    rawDescription && rawDescription.trim() !== "." && rawDescription.trim() !== ""
      ? rawDescription
      : "No description added yet.";

  return (
    <div className="profile-content-page">
      {/* CLEAN PAGE HEADER */}
      <div className="profile-header-strip">
        <div className="profile-header-strip_info">
          <h2>Profile Details</h2>
          <p>Manage account settings, contact information, and workplace details.</p>
        </div>
        <div className="profile-header-strip_actions">
          <Buttons
            text="Edit Profile"
            type="button"
            variant="primary"
            onClick={handleEditForm}
            style={{ height: 38 }}
            icon={<EditIcon fontSize="small" />}
          />
        </div>
      </div>

      {/* USER OVERVIEW CARD */}
      <div className="profile-user-card">
        <UserAvatar
          name={profileName || "User"}
          sx={{ width: 64, height: 64, fontSize: 24, backgroundColor: "#104581" }}
        />
        <div className="profile-user-card_info">
          <h3>{profileName || profileRole}</h3>
          <p className="profile-user-card_role">{profileRole} • {workplace}</p>
          {primaryEmail && <p className="profile-user-card_email">{primaryEmail}</p>}
        </div>
      </div>

      {/* INFORMATION CARDS GRID */}
      <div className="profile-sections-grid">
        {/* CONTACT INFORMATION */}
        <div className="profile-card">
          <div className="profile-card_header">
            <div className="profile-card_title">
              <EmailIcon className="profile-icon" />
              <h3>Contact Information</h3>
            </div>
          </div>

          <div className="profile-card_body">
            <div className="profile-info-grid">
              {profileName && (
                <div className="profile-info-item">
                  <span className="profile-info-label">
                    <PersonIcon fontSize="small" /> Full Name
                  </span>
                  <span className="profile-info-value">{profileName}</span>
                </div>
              )}

              {username && (
                <div className="profile-info-item">
                  <span className="profile-info-label">
                    <PersonIcon fontSize="small" /> Username
                  </span>
                  <span className="profile-info-value">{username}</span>
                </div>
              )}

              <div className="profile-info-item">
                <span className="profile-info-label">
                  <EmailIcon fontSize="small" /> Primary Email
                </span>
                <div className="profile-info-value-row">
                  <span className="profile-info-value">{primaryEmail || "-"}</span>
                  {primaryEmail && (
                    <button
                      type="button"
                      className="copy-btn"
                      onClick={() => handleCopy(primaryEmail)}
                      title="Copy Email"
                    >
                      {copiedEmail === primaryEmail ? (
                        <CheckIcon fontSize="small" style={{ color: "#10b981" }} />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              {phone && (
                <div className="profile-info-item">
                  <span className="profile-info-label">
                    <PhoneIcon fontSize="small" /> Phone Number
                  </span>
                  <span className="profile-info-value">{phone}</span>
                </div>
              )}
            </div>

            {/* SECONDARY EMAILS */}
            {secondaryEmails && secondaryEmails.length > 0 && (
              <div className="profile-secondary-emails">
                <span className="profile-info-label">Secondary Email Addresses</span>
                <div className="secondary-email-chips">
                  {secondaryEmails.map((email) => (
                    <div key={email} className="email-chip">
                      <span>{email}</span>
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => handleCopy(email)}
                        title="Copy Email"
                      >
                        {copiedEmail === email ? (
                          <CheckIcon fontSize="small" style={{ color: "#10b981" }} />
                        ) : (
                          <ContentCopyIcon fontSize="small" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* WORKPLACE CONFIGURATION */}
        <div className="profile-card">
          <div className="profile-card_header">
            <div className="profile-card_title">
              <BusinessIcon className="profile-icon" />
              <h3>Workplace Details</h3>
            </div>
          </div>

          <div className="profile-card_body">
            <div className="profile-info-grid">
              <div className="profile-info-item">
                <span className="profile-info-label">
                  <BusinessIcon fontSize="small" /> Workplace Name
                </span>
                <span className="profile-info-value">{workplace}</span>
              </div>

              <div className="profile-info-item">
                <span className="profile-info-label">
                  <BadgeIcon fontSize="small" /> Prefix Code
                </span>
                <span className="profile-info-value">{prefix}</span>
              </div>

              <div className="profile-info-item">
                <span className="profile-info-label">Assigned Users Count</span>
                <span className="profile-info-value">{userCount}</span>
              </div>

              <div className="profile-info-item">
                <span className="profile-info-label">Position / Role</span>
                <span className="profile-info-value">{profileRole}</span>
              </div>
            </div>
          </div>
        </div>

        {/* DESCRIPTION */}
        <div className="profile-card">
          <div className="profile-card_header">
            <div className="profile-card_title">
              <DescriptionIcon className="profile-icon" />
              <h3>Professional Summary</h3>
            </div>
          </div>

          <div className="profile-card_body">
            <div className="profile-description-box">
              <p>{cleanDescription}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetails;


